import { format } from 'date-fns';
import { ReactElement, useCallback, useMemo } from 'react';
import { DefaultZIndexes, ReferenceLine, XAxis, XAxisProps } from 'recharts';

import CustomTickX from '@/pages/Insights/components/Chart/CustomTickX';

const MINUTE = 1000 * 60;
const DAY = MINUTE * 60 * 24;

interface UseXAxisConfig extends XAxisProps {
  chartEnd: number;
  chartStart: number;
  dataKey: string | ((item: any) => any);
  tickCount?: number;
  yAxisId?: string | number;
}

const useXAxis = ({
  chartEnd,
  chartStart,
  dataKey,
  tickCount = 5,
  yAxisId,
  ...restXAxisProps
}: UseXAxisConfig): {
  xAxisDomain: [number, number];
  xAxis: ReactElement;
  referenceLines: ReactElement[];
} => {
  const domain = useMemo<[number, number]>(() => [chartStart, chartEnd], [chartStart, chartEnd]);

  const timeDiff = chartEnd - chartStart;

  const ticks = useMemo(() => {
    const period = timeDiff / (tickCount - 1);

    const items = [chartStart];
    for (let i = 1; i < tickCount - 1; i++) {
      const t = chartStart + i * period;
      items.push(t);
    }
    items.push(chartEnd);

    return items;
  }, [timeDiff, tickCount, chartStart, chartEnd]);

  const tickFormat = timeDiff < DAY * 1.9 ? 'HH:mm' : 'LLL d';

  const tickFormatter = useCallback(
    (date: number, index: number) => {
      if (index === ticks.length - 1) {
        return '';
      }

      const rounded = Math.round(date / MINUTE) * MINUTE;
      return format(rounded, tickFormat);
    },
    [ticks, tickFormat],
  );

  const referenceLines = useMemo(
    () =>
      ticks
        .slice(0, -1)
        .map((date, index) => (
          <ReferenceLine
            key={date.toString()}
            x={date}
            stroke="var(--colors-border\/regular)"
            strokeDasharray={index !== 0 ? '6 6' : undefined}
            zIndex={DefaultZIndexes.bar - 1}
            yAxisId={yAxisId}
          />
        )),
    [ticks, yAxisId],
  );

  return {
    xAxisDomain: domain,
    xAxis: (
      <XAxis
        domain={domain}
        scale="time"
        type="number"
        interval={0}
        ticks={ticks}
        tickFormatter={tickFormatter}
        tick={<CustomTickX />}
        allowDataOverflow
        dataKey={dataKey}
        {...restXAxisProps}
        axisLine={{ stroke: 'var(--colors-border\\/hover)' }}
        tickLine={{ stroke: 'var(--colors-border\\/hover)' }}
      />
    ),
    referenceLines,
  };
};

export default useXAxis;
